import React, { useEffect, useMemo } from "react";
import {
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  createMembershipPlan,
  getMembershipPlanCountries,
  getMembershipPlanSecurityProviders,
  updateMembershipPlan,
} from "../../../store/slices/membershipPlansSlice";

const normalizeSecurityItems = (items) =>
  items
    .flatMap((item) => {
      if (typeof item !== "string") {
        return item;
      }

      const trimmedItem = item.trim();

      if (trimmedItem.startsWith("[") && trimmedItem.endsWith("]")) {
        try {
          const parsedItem = JSON.parse(trimmedItem);
          return Array.isArray(parsedItem) ? parsedItem : [parsedItem];
        } catch (error) {
          return trimmedItem
            .replace(/^\[/, "")
            .replace(/\]$/, "")
            .split(",");
        }
      }

      return trimmedItem;
    })
    .map((item) => (typeof item === "string" ? item.replace(/^"|"$/g, "").trim() : item))
    .filter(Boolean);

const normalizeSecurityString = (value) => {
  const trimmedValue = value.trim();

  if (trimmedValue.startsWith("[") && trimmedValue.endsWith("]")) {
    try {
      const parsedValue = JSON.parse(trimmedValue);
      return Array.isArray(parsedValue)
        ? normalizeSecurityItems(parsedValue)
        : normalizeSecurityItems([parsedValue]);
    } catch (error) {
      return normalizeSecurityItems([
        trimmedValue
          .replace(/^\[/, "")
          .replace(/\]$/, ""),
      ]);
    }
  }

  return normalizeSecurityItems(
    trimmedValue
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
  );
};

const getUniqueSecurityItems = (items) => [...new Set(items)];

const normalizeSecurityProviderIds = (items) =>
  getUniqueSecurityItems(
    (Array.isArray(items) ? items : [])
      .map((item) => {
        const normalizedValue = Number(item);
        return Number.isNaN(normalizedValue) ? item : normalizedValue;
      })
      .filter((item) => item !== undefined && item !== null && item !== "")
  );

const getSecurityDefaults = (editItem) => {
  if (Array.isArray(editItem?.security_provider_ids)) {
    return normalizeSecurityProviderIds(editItem.security_provider_ids);
  }

  if (Array.isArray(editItem?.security_providers)) {
    return normalizeSecurityProviderIds(
      editItem.security_providers
        .map((provider) => provider?.id)
        .filter((id) => id !== undefined && id !== null)
    );
  }

  if (Array.isArray(editItem?.security_options)) {
    return getUniqueSecurityItems(normalizeSecurityItems(editItem.security_options));
  }

  if (typeof editItem?.security_options === "string") {
    return getUniqueSecurityItems(normalizeSecurityString(editItem.security_options));
  }

  if (Array.isArray(editItem?.security)) {
    return getUniqueSecurityItems(normalizeSecurityItems(editItem.security));
  }

  if (typeof editItem?.security === "string") {
    return getUniqueSecurityItems(normalizeSecurityString(editItem.security));
  }

  return [];
};

const AddMembershipPlanModal = ({ isOpen, toggle, editItem, onSuccess }) => {
  const dispatch = useDispatch();
  const selectedClientValue = useSelector((state) => state.clients.selectedClient.value);
  const countriesData = useSelector((state) => state.membershipPlans.countriesData || []);
  const securityProvidersData = useSelector(
    (state) => state.membershipPlans.securityProvidersData || []
  );
  const countryOptions = useMemo(
    () =>
      (Array.isArray(countriesData) ? countriesData : [])
        .map((country) => country?.name)
        .filter(Boolean)
        .map((name) => ({ label: name, value: name })),
    [countriesData]
  );
  const securityOptions = useMemo(
    () =>
      (Array.isArray(securityProvidersData) ? securityProvidersData : [])
        .filter((provider) => provider?.id !== undefined && provider?.id !== null && provider?.name)
        .map((provider) => ({
          label: provider.name,
          value: provider.id,
        })),
    [securityProvidersData]
  );

  const defaultValues = useMemo(() => {
    if (editItem && Object.keys(editItem).length > 0) {
      return {
        name: editItem.name || editItem.client_name || "",
        country: editItem.country || "",
        status: editItem.status || editItem.client_status || "",
        security_provider_ids: getSecurityDefaults(editItem),
        tenant_slug: editItem.tenant_slug || selectedClientValue || "",
      };
    }

    return {
      name: "",
      country: "",
      status: "",
      security_provider_ids: [],
      tenant_slug: selectedClientValue || "",
    };
  }, [editItem, selectedClientValue]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({
    defaultValues,
    values: defaultValues,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const selectedSecurityProviderIds = watch("security_provider_ids") || [];

  useEffect(() => {
    setValue("tenant_slug", selectedClientValue || editItem?.tenant_slug || "");
  }, [editItem?.tenant_slug, selectedClientValue, setValue]);

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      dispatch(getMembershipPlanCountries());
      dispatch(getMembershipPlanSecurityProviders());
    }
  }, [defaultValues, dispatch, isOpen, reset]);

  const toggleSecurity = (providerId) => {
    const nextValue = selectedSecurityProviderIds.includes(providerId)
      ? selectedSecurityProviderIds.filter((item) => item !== providerId)
      : [...selectedSecurityProviderIds, providerId];

    setValue("security_provider_ids", normalizeSecurityProviderIds(nextValue), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        name: data.name,
        country: data.country,
        status: data.status,
        tenant_slug: data.tenant_slug || selectedClientValue || editItem?.tenant_slug || "",
        security_provider_ids: normalizeSecurityProviderIds(data.security_provider_ids || []),
      };

      if (editItem && Object.keys(editItem).length > 0) {
        const changedValues = {};

        Object.keys(dirtyFields).forEach((key) => {
          changedValues[key] = payload[key];
        });

        if (!Object.prototype.hasOwnProperty.call(changedValues, "tenant_slug")) {
          changedValues.tenant_slug = payload.tenant_slug;
        }

        if (!Object.keys(changedValues).length) {
          toggle();
          return;
        }

        const updatePayload = {
          id: editItem.id || editItem.ID,
          payload: { ...changedValues },
        };

        console.log("Update membership plan payload:", updatePayload);

        const resultAction = await dispatch(updateMembershipPlan(updatePayload));

        if (updateMembershipPlan.fulfilled.match(resultAction)) {
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        } else {
          return;
        }
      } else {
        console.log("Create membership plan payload:", payload);

        const resultAction = await dispatch(createMembershipPlan(payload));

        if (createMembershipPlan.fulfilled.match(resultAction)) {
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        } else {
          return;
        }
      }

      toggle();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        {editItem && Object.keys(editItem).length > 0 ? "Edit Membership Plan" : "Add Membership Plan"}
      </ModalHeader>
      <ModalBody className="pt-4">
        <form id="membership-plan-form" onSubmit={handleSubmit(onSubmit)}>
          <input type="hidden" {...register("tenant_slug")} />

          <div className="px-2">
            <div className="mb-4">
              <Label className="mb-2">Country</Label>
              <select
                className={`form-control ${errors.country ? "is-invalid" : ""}`}
                {...register("country", { required: "Country is required" })}
              >
                <option value="">Select Country</option>
                {countryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.country && <span className="text-danger small">{errors.country.message}</span>}
            </div>

            <div className="mb-4">
              <Label className="mb-2">Name</Label>
              <input
                type="text"
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                placeholder="Enter plan name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && <span className="text-danger small">{errors.name.message}</span>}
            </div>

            <div className="mb-4">
              <Label className="mb-2">Status</Label>
              <select
                className={`form-control ${errors.status ? "is-invalid" : ""}`}
                {...register("status", { required: "Status is required" })}
              >
                <option value="">Select Status</option>
                <option value="Active">Active</option>
                <option value="Deactive">Deactive</option>
              </select>
              {errors.status && <span className="text-danger small">{errors.status.message}</span>}
            </div>

            <div className="mb-2">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Label className="mb-0">Security</Label>
                <span className="text-muted small">Select one or more options</span>
              </div>

              <input
                type="hidden"
                {...register("security_provider_ids", {
                  validate: (value) =>
                    Array.isArray(value) && value.length > 0 ? true : "Select at least one security option",
                })}
              />

              <div className="border rounded-3 p-3" style={{ backgroundColor: "#f8fafc" }}>
                {securityOptions.map((provider) => {
                  const isChecked = selectedSecurityProviderIds.includes(provider.value);

                  return (
                    <div
                      key={provider.value}
                      className="d-flex align-items-center justify-content-between py-2"
                    >
                      <div>
                        <div className="fw-semibold">{provider.label}</div>
                        <div className="text-muted small">Enable this security provider for the plan</div>
                      </div>

                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={isChecked}
                          style={{
                            cursor: "pointer",
                            backgroundColor: isChecked ? "#7367f0" : "#ffffff",
                            borderColor: isChecked ? "#7367f0" : "#94a3b8",
                            boxShadow: "none",
                          }}
                          onChange={() => toggleSecurity(provider.value)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {errors.security_provider_ids && (
                <span className="text-danger small">{errors.security_provider_ids.message}</span>
              )}
            </div>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-primary" onClick={handleSubmit(onSubmit)}>
          {editItem && Object.keys(editItem).length > 0 ? "Update" : "Save"}
        </button>
        <button className="btn btn-danger" onClick={toggle}>
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default AddMembershipPlanModal;